import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { UCFirst, ensureBoolean, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';

import ActionButton from '../builder/ActionButton';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import ColorPicker from '../builder/ColorPicker.js';
import DisabledArea from '../builder/DisabledArea.js';
import DynamicFields from '../builder/DynamicFields.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Model from '../builder/Model';
import Range from '../builder/Range.js';
import RichEditor from '../builder/RichEditor';
import Select from '../builder/Select.js';
import Spinner from '../builder/Spinner';
import TextArea from '../builder/TextArea.js';
import Tooltip from '../builder/Tooltip.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout';
import LikeDislikeClient from '../front/LikeDislikeClient.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import LikeDislikeShare from '../front/LikeDislikeShare.js';

const RichEditorPlaceHolder = injectIntl(transferPlaceholder(RichEditor));
const InputPlaceHolder = injectIntl(transferPlaceholder(Input));

const PlayerField = ({ model, change, ...props }) => {
	const [ uploading, setUploading ] = useState(false);

	const auto_img_hooks = (hook) => {
		switch(hook.type) {
			case 'AUTO_IMAGE_START':
				setUploading(true);
				break;

			case 'AUTO_IMAGE_END':
				setUploading(false);
				break;
		}
	}

	return (
		<Model
			model={model}
			change={change}
			errors={props.errors != undefined && props.errors[model.id]}
		>
			<Input
				name='name'
				max_length={25}
				_class={{ size:60 }}
			>Title</Input>

			<AutoPostImageUploader
				auto_url={`/promotions/auto_img/${props.promotion_id}`}
				auto_type='stages'
				changeMulti={props.changeMulti}
				hooks={auto_img_hooks}
				image={model.full_image}
				name="image"
				small_input={true}
				_size={{ width:400, height:500 }}
				_class={{ size:50 }}
			>Image</AutoPostImageUploader>
		</Model>
	)
};

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors, setErrors ] = useState({});
	const [ loading, setLoading ] = useState(false);
	const [ preview_player_index, setPreviewPlayerIndex ] = useState(1);

	useEffect(() => {
		if(stage.stack_milestones == undefined) validate([]);
	}, []);

	const validate = (v) => {
		return true;

		v = (v == undefined) ? stage.smash_images : v;
		let errors = {};
		let total_images = v.length; //stage.smash_images != undefined ? Object.keys(stage.smash_images).length : 0;
		let errors_images = false;

		if(total_images < num_elements) {
			const remaining_images = num_elements - total_images;

			if(remaining_images > 1) {
				errors['smash_images'] = props.intl.formatMessage({ id:"Still missing {remain} items" }, { remain:remaining_images });
			} else {
				errors['smash_images'] = i18n("Still missing 1 item");
			}
		}

		if(errors_images) errors['images'] = i18n("Some items are missing information");

		setErrors(errors);

		setLoading(true);

		setTimeout(() => {
			setLoading(false);
		}, 0);
	};

	const changeSidebar = (k, v) => {
		change(k, v);
		//validate();
	};

	const max_players = 15;

	const players = stage?.likedislike_players ?? [];
	const section_title = `${i18n("Options")} (${players.length}/${max_players})`;

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	const dynamic_fields = [{
		type:'player',
		label:"Option",
		translate:[{
			key:'name',
			type:'input',
		}],
		title:(f) => {
			return (isNotEmpty(f, 'name')) ? f.name : `Option`;
		},
		add_options:['images'],
	}];

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={changeSidebar}>
						<Checkbox
							name="likedislike_random_display"
							value={ensureBoolean(stage?.likedislike_random_display ?? 'false')}
							_class={{ first:true }}
						>Display options randomly</Checkbox>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<DynamicFields
						section_title={section_title}
						onChange={(v) => changeSidebar('likedislike_players', v)}
						field_types={dynamic_fields}
						has_translation={true}
						locales={avail_locales}
						add_upload={{
							auto_url:`/promotions/auto_img/${stage.promotion_id}`,
							auto_type:'stages',
							contract:{ name:'title', image:'image' }
						}} 
						add_button_props={{
							icon:'knk-add-item mainButton',
						}}
						img_size={{
							width:400,
							height:500,
						}}
						add_options={true}
						fields={players}
						ul_classes='gui_overflowable'
						ul_styles={{ maxHeight:'calc(100dvh - 285px)' }}
						thumb_h={34}
						max_items={max_players}
						sortable={!ensureBoolean(stage?.likedislike_random_display ?? 'false')}
						nameAutoTransform={(v) => {
							return UCFirst(v.replace(/[\-\_]/gi, " "));
						}}
						onClickOpen={(index) => {
							if(index === false) return;
							setPreviewPlayerIndex(index);
						}}
					>
						<PlayerField
							promotion_id={stage.promotion_id}
							errors={errors}
						/>
					</DynamicFields>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<LikeDislikeClient
									stage={{ Stage:stage }}
									promotion={props.promotion}
									is_builder={true}
									is_playable={true}
									section="settings"
									preview_index={preview_player_index}
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;
											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const [ errors, setErrors ] = useState({});
	const [ loading, setLoading ] = useState(false);

	useEffect(() => {
		if(stage.stack_milestones == undefined) validate([]);
	}, []);

	const validate = (v) => {
		return true;

		v = (v == undefined) ? stage.smash_images : v;
		let errors = {};
		let total_images = v.length; //stage.smash_images != undefined ? Object.keys(stage.smash_images).length : 0;
		let errors_images = false;

		if(total_images < num_elements) {
			const remaining_images = num_elements - total_images;

			if(remaining_images > 1) {
				errors['smash_images'] = props.intl.formatMessage({ id:"Still missing {remain} items" }, { remain:remaining_images });
			} else {
				errors['smash_images'] = i18n("Still missing 1 item");
			}
		}

		if(errors_images) errors['images'] = i18n("Some items are missing information");

		setErrors(errors);

		setLoading(true);

		setTimeout(() => {
			setLoading(false);
		}, 0);
	};

	const changeSidebar = (k, v) => {
		change(k, v);
		//validate();
	};

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={changeSidebar}>
						<RichEditor
							name="main_description"
							_class={{ first:true }}
						>Introduction message</RichEditor>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Like options"
						icon='ep-thumbs-up-empty'
					/>

					<Model model={stage} change={changeSidebar}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name="likedislike_like_image"
							image={stage.full_likedislike_like_image}
							display_custom_dragging_instructions="Upload the image"
							small_input={true}
							_size={{ width:200, height:200 }}
						>Icon image</AutoPostImageUploader>

						<ColorPicker
							_class={{ size:50 }}
							_hide_code={true}
							name="likedislike_like_color"
							color={stage?.likedislike_like_color ?? 'rgba(120, 195, 0, 1)'}
						>Highlight color</ColorPicker>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Dislike options"
						icon='ep-thumbs-down-empty'
					/>

					<Model model={stage} change={changeSidebar}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name="likedislike_dislike_image"
							image={stage.full_likedislike_dislike_image}
							display_custom_dragging_instructions="Upload the image"
							small_input={true}
							_size={{ width:200, height:200 }}
						>Icon image</AutoPostImageUploader>

						<ColorPicker
							_class={{ size:50 }}
							_hide_code={true}
							name="likedislike_dislike_color"
							color={stage?.likedislike_dislike_color ?? 'rgba(215, 35, 60, 1)'}
						>Highlight color</ColorPicker>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Cards"
						icon='knk-sticker'
					/>

					<Model model={stage} change={changeSidebar}>
						<Range
							min={0}
							max={50}
							step={5}
							_class={{ size:50 }}
							name="likedislike_border_radius"
							value={stage?.likedislike_border_radius ?? 30}
						>Border radius</Range>

						<Range
							min={0}
							max={5}
							step={1}
							_class={{ size:50 }}
							name="likedislike_border_width"
							value={stage?.likedislike_border_width ?? 1}
						>Border width</Range>

						{((stage?.likedislike_border_width ?? 1) > 0) && (
							<ColorPicker
								_class={{ size:50 }}
								_hide_code={true}
								name="likedislike_border_color"
								color={stage?.likedislike_border_color ?? 'rgba(0, 0, 0, 1)'}
							>Border color</ColorPicker>
						)}

						<Checkbox
							name="likedislike_display_player_text"
							value={stage?.likedislike_display_player_text ?? true}
						>Show option title</Checkbox>

						{(stage?.likedislike_display_player_text ?? true) && (
							<ColorPicker
								name="text_color"
								_class={{ size:50 }}
								_hide_code={true}
								color={stage?.text_color ?? 'rgba(0, 0, 0, 1)'}
							>Text color</ColorPicker>
						)}

						{(stage?.likedislike_display_player_text ?? true) && (
							<Range
								min={0}
								max={5}
								name="likedislike_text_border_width"
								default={0}
								step={1}
								_class={{ size:50 }}
								value={parseInt(stage?.likedislike_text_border_width ?? '0')}
								label_props={{
									_extra_component:i18n("in 'px'"),
								}}
							>Text border width</Range>
						)}

						<ColorPicker
							_class={{ size:50 }}
							_hide_code={true}
							name="likedislike_card_bg_color"
							color={stage?.likedislike_card_bg_color ?? (stage?.bg_color ?? 'rgba(255, 255, 255, 1)')}
						>Background color</ColorPicker>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Board"
						icon='knk-sticker'
					/>

					<Model model={stage} change={changeSidebar}>
						<ColorPicker
							name="bg_color"
							_class={{ size:50 }}
							_hide_code={true}
							color={stage?.bg_color ?? 'rgba(255, 255, 255, 1)'}
						>Background color</ColorPicker>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_bg_image}
							name="bg_image"
							small_input={true}
							_size={{ width:800, height:800 }}
						>Background image</AutoPostImageUploader>

						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<LikeDislikeClient
									stage={{ Stage:stage }}
									promotion={props.promotion}
									is_builder={true}
									is_playable={false}
									section="design"
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;
											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameFinal = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const changeSidebar = (k, v) => {
		change(k, v);
		//validate();
	};

	const [ filters, setFilters ] = useState({ view_page:'score' });

	const main_description_placeholder = `<h1>${i18n("Results")}</h1>`;
	const main_shared_placeholder = `<h1>${i18n("The choice of {name}")}</h1><p>${i18n("Join in, select your favorite option, and check how the results are going.")}</p>`;

	// by default, all networks are enabled, so if promotion.config.share is not defined, we set up the networks defaults to '1' (i.e. true):
	const share_options = props?.promotion?.config?.share?.networks ?? { twitter:'1', whatsapp:'1', line:'1', telegram:'1' };

	const has_twitter_enabled = (share_options.twitter == '1') ? true : false;
	const has_mobile_enabled = (share_options.whatsapp == '1' || share_options.line == '1' || share_options.telegram == '1') ? true : false;

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Model model={filters} change={(k, v) => setFilters({ ...filters, [k]:v })}>
					<Select
						name='view_page'
						values={[
							{ k:'score', v:i18n("Page after selecting the favorite option") },
							{ k:'public_share', v:i18n("Destination page of the shared link"), hide:(stage?.share_enabled ?? true) ? false : true },
						]}
						_class={{first:true}}
					>Select the page to edit</Select>
				</Model>

				{(filters.view_page == 'score') && (
					<>
						<article class="gui_not_in_top_huge">
							<Model model={stage} change={changeSidebar}>
								<RichEditorPlaceHolder
									name='final_message'
									placeholder={main_description_placeholder}
								>Introduction message</RichEditorPlaceHolder>
							</Model>
						</article>

						<article class="gui_not_in_top_huge">
							<BuilderSubSectionTitle
								title="Chart with results"
								icon='knk-stats'
							/>

							<Model model={stage} change={changeSidebar}>
								<Checkbox
									name="likedislike_display_stats"
									value={stage?.likedislike_display_stats ?? true}
								>Show the chart with results</Checkbox>
							</Model>
						</article>

						<article class="gui_not_in_top_huge">
							<BuilderSubSectionTitle
								title="Favorite option"
								icon='knk-share'
							/>

							<Model model={stage} change={changeSidebar}>
								<Checkbox
									name="share_enabled"
									value={stage?.share_enabled ?? true}
								>Show the favorite option to share</Checkbox>
							</Model>

							{(stage?.share_enabled ?? true) && (
								<Model model={stage} change={changeSidebar}>
									<AutoPostImageUploader
										auto_url={`/promotions/auto_img/${stage.promotion_id}`}
										auto_type='stages'
										image={stage.full_game_final_bg_image}
										name="game_final_bg_image"
										small_input={true}
										_size={{ width:800, height:1120 }}
									>Background image</AutoPostImageUploader>
								</Model>
							)}
						</article>

						{(stage?.share_enabled ?? true) && (
							<article class="gui_not_in_top_huge">
								<BuilderSubSectionTitle
									title="Content when sharing"
									icon='knk-share'
								/>
									<Model model={stage} change={changeSidebar}>
										<Input
											name="share_title"
											max_length={90}
											label_props={{
												tooltip:<Tooltip>This is the title of the publication of the viral message on Facebook. Maximum 90 characters.</Tooltip>,
											}}
										>Title</Input>

										<TextArea
											name="share_description"
											max_length={255}
											label_props={{
												tooltip:<Tooltip>This is the description of the publication of the viral message on Facebook. Maximum 255 characters.</Tooltip>,
											}}
										>Description</TextArea>
									</Model>

									<DisabledArea
										disabled={(!has_twitter_enabled) ? true : false}
										message="Twitter network must be enabled"
										link='#share'
									>
										<Model model={stage} change={changeSidebar}>
											<TextArea
												name="share_tweet"
												max_length={280}
												label_props={{
													tooltip:<Tooltip>This is the text of the tweet of the viral message on Twitter. The shared link will be included automatically at the end of this text.</Tooltip>,
												}}
											>Tweet</TextArea>
										</Model>
									</DisabledArea>

									<DisabledArea
										disabled={(!has_mobile_enabled) ? true : false}
										message="Whatsapp, Telegram or Line networks must be enabled"
										link='#share'
									>
										<Model model={stage} change={changeSidebar}>
											<TextArea
												name="share_instant"
												max_length={200}
												label_props={{
													tooltip:<Tooltip>This is the message that users will send through Instant Messaging Apps. The shared link will be automatically included at the end of the mensaje. This option is only available for users connnected via smartphone. Maximum length: 200 characters</Tooltip>,
												}}
											>Instant Messaging Apps (Whatsapp, Telegram, Line)</TextArea>
										</Model>
									</DisabledArea>
							</article>
						)}
					</>
				)}

				{(filters.view_page == 'public_share') && (
					<article class="gui_not_in_top_huge">
						<Model model={stage} change={changeSidebar}>
							<RichEditorPlaceHolder
								name='game_shared_description'
								placeholder={main_shared_placeholder}
							>Introduction message</RichEditorPlaceHolder>

							<InputPlaceHolder
								name='game_shared_button_label'
								placeholder={i18n("Make your choice")}
							>{i18n("Label for '%s' button").replace('%s', i18n("Make your choice"))}</InputPlaceHolder>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			<BuilderCanvas is_adaptable={true} fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
					display_header={filters.view_page == 'score' ? true : false}
				>
					<div class="___gui_withoutEvents" style={{ width:'100%', height:'100%' }}>
						{(filters.view_page == 'score') && (
							<LikeDislikeClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								is_playable={false}
								section="score"
							/>
						)}

						{(filters.view_page == 'public_share') && (
							<LikeDislikeShare
								stage={stage}
								promotion={props.promotion}
								is_builder={true}
							/>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar without_ctas={true}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<div style={{width:'100%'}}>
						<LikeDislikeClient
							stage={{ Stage:stage }}
							promotion={props.promotion}
							is_builder={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const LikeDislikeBuilder = ({ base_route, stage, ...props }) => {
	const basename = `/stages/${stage.id}/likedislike`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Options", link:`${basename}/settings`, icon:'knk-dimensional-portal' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Final screen", link:`${basename}/final`, icon:'knk-on' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/final">
						<GameFinal
							{...common_props}
							nav_active={`/final`}
						/>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
};

export default injectIntl(LikeDislikeBuilder);